import React from "react";
import { motion } from "framer-motion";

interface PulseButtonProps {
  children: React.ReactNode;
  onClick?: (
    e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
  ) => void;
  href?: string;
  className?: string;
  variant?: "rounded" | "rounded-lg";
  size?: "sm" | "md" | "lg";
  disabled?: boolean;
}

const PulseButton: React.FC<PulseButtonProps> = ({
  children,
  onClick,
  href,
  className = "",
  variant = "rounded",
  size = "md",
  disabled = false,
}) => {
  const sizeClasses = {
    sm: "px-3 py-2 text-sm",
    md: "px-4 py-2.5 md:px-6 md:py-3 text-sm md:text-base",
    lg: "px-6 py-3 md:px-8 md:py-4 text-base md:text-lg",
  };

  const variantClasses = {
    rounded: "rounded-full",
    "rounded-lg": "rounded-lg",
  };

  const innerContent = (
    <>
      {/* Pulse Animation Circles */}
      <motion.div
        className={`absolute inset-0 ${variantClasses[variant]} bg-gradient-to-r from-indigo-500 to-purple-600`}
        animate={{
          scale: [1, 1.8, 1],
          opacity: [0.15, 0, 0.15],
        }}
        transition={{
          duration: 0.9,
          ease: "easeInOut",
          repeat: Infinity,
          repeatDelay: 0.8,
        }}
      />

      <motion.div
        className={`absolute inset-0 ${variantClasses[variant]} bg-gradient-to-r from-indigo-500 to-purple-600`}
        animate={{
          scale: [1, 1.6, 1],
          opacity: [0.05, 0, 0.05],
        }}
        transition={{
          duration: 0.9,
          ease: "easeInOut",
          repeat: Infinity,
          repeatDelay: 0.8,
          delay: 0.45,
        }}
      />

      {/* Glowing Border Animation */}
      <div
        className={`absolute inset-0 ${variantClasses[variant]} bg-gradient-to-r from-indigo-500 to-purple-600`}
        style={{
          background:
            "conic-gradient(from 0deg, transparent 0deg, #00d4ff 90deg, transparent 180deg, transparent 360deg)",
          animation: "glow-rotate 1s linear infinite",
        }}
      />

      {/* Button Content */}
      <div
        className={`relative flex items-center justify-center gap-2 ${sizeClasses[size]} ${variantClasses[variant]} bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold shadow-md hover:shadow-lg transition-all duration-300 ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
      >
        {children}
      </div>
    </>
  );

  if (href) {
    return (
      <motion.a
        href={href}
        className={`relative group inline-flex items-center justify-center ${className}`}
        onClick={onClick}
        whileHover={!disabled ? { scale: 1.02, y: -1 } : {}}
        whileTap={!disabled ? { scale: 0.98 } : {}}
      >
        {innerContent}
      </motion.a>
    );
  }

  return (
    <motion.button
      className={`relative group ${className}`}
      onClick={onClick}
      disabled={disabled}
      whileHover={!disabled ? { scale: 1.02, y: -1 } : {}}
      whileTap={!disabled ? { scale: 0.98 } : {}}
    >
      {innerContent}
    </motion.button>
  );
};

export default PulseButton;
